/**
 * Janela separada para assistir a uma transmissão de tela.
 *
 * Antes, assistir era uma camada por cima do app: a transmissão tomava a
 * janela inteira e o LFRCORD sumia atrás dela. Quem estava assistindo não
 * conseguia ler o canal, responder ninguém, nem olhar quem estava na sala sem
 * fechar o que estava vendo — e numa tela só, isso é escolher entre assistir e
 * participar.
 *
 * Uma janela de verdade resolve as duas de uma vez: vai para o outro monitor,
 * fica ao lado, encosta na metade da tela, some com Alt-Tab e volta. O
 * sistema operacional já sabe fazer tudo isso; a camada dentro do app não
 * sabia fazer nada disso.
 *
 * O vídeo é o MESMO objeto `MediaStream` da janela principal, não uma segunda
 * conexão: a janela nova só desenha o que já está chegando. Nada extra trafega
 * na rede, e fechar a janela não derruba a transmissão para ninguém.
 *
 * O áudio continua saindo pela janela principal, onde já existe o controle de
 * volume por pessoa e a escolha do dispositivo de saída. Por isso o vídeo aqui
 * nasce mudo: com som nos dois lugares, tudo sairia dobrado.
 */

export type JanelaDeTela = {
  /** Troca a trilha exibida sem piscar a janela (a pessoa reiniciou a captura). */
  atualizar: (stream: MediaStream | null) => void;
  /** Traz a janela para a frente — o clique de novo em quem já está aberto. */
  focar: () => void;
  /** Fecha a janela; seguro de chamar duas vezes. */
  fechar: () => void;
  /** A janela ainda está aberta? */
  aberta: () => boolean;
};

/**
 * `frameName` é o que o app desktop usa para reconhecer esta janela.
 *
 * O Electron manda todo `window.open` para o navegador do sistema — é como o
 * login do Google funciona. Sem um nome combinado, "assistir" abriria o
 * Chrome numa página em branco. O `main.ts` procura este prefixo e, só nele,
 * abre uma janela do próprio app.
 */
const NOME_DA_JANELA = "lfrcord-tela";

/** Uma janela por transmissão: reabrir a mesma pessoa reaproveita a janela. */
function nomeDe(peerId: string): string {
  return `${NOME_DA_JANELA}-${peerId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

export function abrirJanelaDeTela(opcoes: {
  peerId: string;
  titulo: string;
  stream: MediaStream | null;
  /** Chamado quando a pessoa fecha a janela — inclusive pelo X do sistema. */
  aoFechar: () => void;
}): JanelaDeTela | null {
  const { peerId, titulo, stream, aoFechar } = opcoes;

  const janela = window.open("", nomeDe(peerId), "popup=yes,width=1280,height=740");
  // Bloqueador de pop-up, ou o app desktop recusando: quem chamou precisa
  // saber para cair na tela cheia de dentro do app em vez de não fazer nada.
  if (!janela) return null;

  /*
    Daqui ate o fim da montagem, tudo pode falhar de um jeito so: a janela
    abrir e o documento dela nao ficar acessivel (politica do navegador, um
    `about:blank` que caiu noutro processo). O desfecho certo nesse caso e
    fechar e devolver `null` — quem chamou cai na tela cheia de dentro do app.
    Uma janela em branco aberta seria o pior dos dois mundos.
  */
  try {
    return montar(janela, titulo, stream, aoFechar);
  } catch {
    try {
      janela.close();
    } catch {
      /* ja fechada, ou fora de alcance: nao ha mais nada a fazer */
    }
    return null;
  }
}

function montar(
  janela: Window,
  titulo: string,
  stream: MediaStream | null,
  aoFechar: () => void,
): JanelaDeTela {
  const doc = janela.document;
  doc.title = `${titulo} — LFRCORD`;

  // A janela é reaproveitada quando já existia (mesmo nome). Limpar antes
  // evita empilhar um segundo vídeo por cima do primeiro.
  doc.body.replaceChildren();

  const estilo = doc.createElement("style");
  estilo.textContent = `
    html, body { margin: 0; height: 100%; background: #0d0d0d; overflow: hidden; }
    body { display: flex; }
    video { width: 100%; height: 100%; object-fit: contain; background: #0d0d0d; }
    .aviso {
      position: fixed; inset: 0; display: grid; place-items: center;
      font: 500 12px/1.5 system-ui, sans-serif; letter-spacing: .12em;
      text-transform: uppercase; color: #6b6b6b; pointer-events: none;
    }
  `;
  doc.head.replaceChildren(estilo);

  const aviso = doc.createElement("div");
  aviso.className = "aviso";
  aviso.textContent = "aguardando vídeo";

  const video = doc.createElement("video");
  video.autoplay = true;
  video.playsInline = true;
  // Mudo de propósito: o som desta transmissão já está tocando na janela
  // principal, com o volume por pessoa e a saída de áudio escolhida lá.
  video.muted = true;

  doc.body.replaceChildren(video, aviso);

  const aplicar = (proximo: MediaStream | null): void => {
    // Sem esta comparacao a janela piscava: reatribuir o MESMO `MediaStream`
    // reinicia a carga do elemento, e a sincronia roda a cada render da sala.
    if (video.srcObject === proximo) return;
    video.srcObject = proximo;
    aviso.style.display = proximo ? "none" : "grid";
    if (!proximo) return;
    // Alguns navegadores recusam o play automático mesmo mudo; a falha aqui é
    // silenciosa de propósito — o clique da pessoa na janela destrava.
    void video.play().catch(() => undefined);
  };
  aplicar(stream);

  let viva = true;
  const encerrar = (): void => {
    if (!viva) return;
    viva = false;
    window.clearInterval(vigia);
    window.removeEventListener("beforeunload", fecharJunto);
    aoFechar();
  };

  /*
    `unload` na janela filha é o aviso normal de fechamento, mas ele não chega
    quando o sistema mata a janela de outro jeito. A checagem por intervalo é o
    que garante que o app volte ao normal em qualquer desfecho — meio segundo é
    rápido o bastante para ninguém notar e barato o bastante para não pesar.
  */
  janela.addEventListener("pagehide", encerrar);
  const vigia = window.setInterval(() => {
    if (janela.closed) encerrar();
  }, 500);

  /** Fechar o app não pode deixar uma janela órfã na barra de tarefas. */
  const fecharJunto = (): void => janela.close();
  window.addEventListener("beforeunload", fecharJunto);

  return {
    atualizar: proximo => {
      if (!viva || janela.closed) return;
      aplicar(proximo);
    },
    focar: () => {
      if (!janela.closed) janela.focus();
    },
    fechar: () => {
      if (janela.closed) {
        encerrar();
        return;
      }
      janela.close();
      encerrar();
    },
    aberta: () => viva && !janela.closed,
  };
}
